import { useEffect, useRef, type RefObject } from "react";
import { draggable } from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { setCustomNativeDragPreview } from "@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview";
import { preserveOffsetOnSource } from "@atlaskit/pragmatic-drag-and-drop/element/preserve-offset-on-source";
import {
  DATA_ROOM_DRAG_TYPE,
  isDataRoomDragData,
  resolveMoveSelection,
  type DataRoomDragItemType,
} from "./types";
import { playCollapseIntoDragAnimation } from "./collapseIntoDrag";

type UseDataRoomItemDragArgs = {
  itemType: DataRoomDragItemType;
  itemId: string;
  itemName: string;
  enabled: boolean;
  selectedFiles: string[];
  selectedFolders: string[];
};

function buildDragPreview(args: {
  container: HTMLElement;
  sourceElement: HTMLElement;
  itemName: string;
  count: number;
}) {
  const wrapper = document.createElement("div");
  wrapper.style.position = "relative";
  wrapper.style.minWidth = "180px";
  wrapper.style.maxWidth = "240px";

  if (args.count > 1) {
    const stack = document.createElement("div");
    stack.style.position = "absolute";
    stack.style.top = "-2px";
    stack.style.left = "-2px";
    stack.style.width = "100%";
    stack.style.height = "100%";
    stack.style.background = "#f9fafb";
    stack.style.border = "1px solid #d1d5db";
    stack.style.borderRadius = "8px";
    wrapper.appendChild(stack);

    const badge = document.createElement("div");
    badge.textContent = String(args.count);
    badge.style.position = "absolute";
    badge.style.zIndex = "10";
    badge.style.top = "-8px";
    badge.style.right = "-8px";
    badge.style.background = "rgb(var(--primary-500))";
    badge.style.color = "white";
    badge.style.fontSize = "12px";
    badge.style.fontWeight = "600";
    badge.style.borderRadius = "9999px";
    badge.style.width = "20px";
    badge.style.height = "20px";
    badge.style.display = "flex";
    badge.style.alignItems = "center";
    badge.style.justifyContent = "center";
    wrapper.appendChild(badge);
  }

  const card = document.createElement("div");
  card.style.position = "relative";
  card.style.background = "white";
  card.style.border = "1px solid #d1d5db";
  card.style.borderRadius = "8px";
  card.style.padding = "10px 14px";
  card.style.display = "flex";
  card.style.alignItems = "center";
  card.style.gap = "10px";
  card.style.boxShadow = "0 4px 12px rgba(0,0,0,0.12)";

  const nameRow = args.sourceElement.querySelector(
    ".flex.items-center.gap-x-2.relative",
  );
  const sourceIcon =
    nameRow?.querySelector(":scope > svg, :scope > img") ??
    nameRow?.firstElementChild?.querySelector("svg, img") ??
    null;
  if (sourceIcon) {
    const clonedIcon = sourceIcon.cloneNode(true) as HTMLElement;
    clonedIcon.setAttribute("width", "20");
    clonedIcon.setAttribute("height", "20");
    clonedIcon.style.width = "20px";
    clonedIcon.style.height = "20px";
    clonedIcon.style.flexShrink = "0";
    card.appendChild(clonedIcon);
  }

  const label = document.createElement("span");
  label.textContent = args.itemName;
  label.style.fontSize = "14px";
  label.style.fontWeight = "600";
  label.style.color = "#111827";
  label.style.overflow = "hidden";
  label.style.textOverflow = "ellipsis";
  label.style.whiteSpace = "nowrap";
  card.appendChild(label);

  wrapper.appendChild(card);
  args.container.appendChild(wrapper);
}

export function useDataRoomItemDrag(
  args: UseDataRoomItemDragArgs,
): RefObject<HTMLTableRowElement> {
  const ref = useRef<HTMLTableRowElement>(null);
  const selectedFilesRef = useRef(args.selectedFiles);
  const selectedFoldersRef = useRef(args.selectedFolders);
  selectedFilesRef.current = args.selectedFiles;
  selectedFoldersRef.current = args.selectedFolders;

  useEffect(() => {
    const element = ref.current;
    if (!element || !args.enabled) {
      return;
    }

    return draggable({
      element,
      getInitialData: () => {
        const selection = resolveMoveSelection({
          itemType: args.itemType,
          itemId: args.itemId,
          selectedFiles: selectedFilesRef.current,
          selectedFolders: selectedFoldersRef.current,
        });

        return {
          [DATA_ROOM_DRAG_TYPE]: true,
          itemType: args.itemType,
          itemId: args.itemId,
          itemName: args.itemName,
          fileIds: selection.fileIds,
          folderIds: selection.folderIds,
        };
      },
      onGenerateDragPreview: ({ nativeSetDragImage, source, location }) => {
        const selection = resolveMoveSelection({
          itemType: args.itemType,
          itemId: args.itemId,
          selectedFiles: selectedFilesRef.current,
          selectedFolders: selectedFoldersRef.current,
        });
        const count = selection.fileIds.length + selection.folderIds.length;

        setCustomNativeDragPreview({
          nativeSetDragImage,
          getOffset: preserveOffsetOnSource({
            element: source.element,
            input: location.current.input,
          }),
          render: ({ container }) => {
            buildDragPreview({
              container,
              sourceElement: source.element,
              itemName: args.itemName,
              count,
            });
          },
        });
      },
      onDragStart: ({ source, location }) => {
        if (!isDataRoomDragData(source.data)) {
          return;
        }

        const itemIds = [...source.data.folderIds, ...source.data.fileIds];
        const { clientX, clientY } = location.current.input;
        requestAnimationFrame(() => {
          playCollapseIntoDragAnimation({
            itemIds,
            cursor: { x: clientX, y: clientY },
          });
        });
      },
    });
  }, [args.enabled, args.itemId, args.itemName, args.itemType]);

  return ref;
}
